import { nameof } from "ts-simple-nameof";
import { useFormContext } from "react-hook-form";
import styles from "./InventoryNomenclatureFormFields.module.scss";
import { IInventoryItemInputModel } from "../models/inventoryNomenclatureModels";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultTextareaField } from "../../../../common/components/forms/inputs/textareaField/DefaultTextareaField";
import { DefaultFormSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { BarcodeField } from "../../../../common/components/forms/inputs/BarcodeField/BarcodeField";
import { CurrencyType, UnitOfMeasure } from "../../../../common/enums";
import { UnitOfMeasureLabels } from "../../../../common/labels";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";

interface IInventoryNomenclatureFormFieldsProps {
  categories: { id: number; name: string }[];
}

/**
 * React-hook-form fields for an inventory item.
 *
 * UnitOfMeasure and InitialQuantity are set once at creation and can never
 * be edited afterwards — the backend enforces this regardless of what the
 * client sends, and the fields are disabled on edit for clarity.
 */
export const InventoryNomenclatureFormFields = ({
  categories,
}: IInventoryNomenclatureFormFieldsProps) => {
  const { watch } = useFormContext<IInventoryItemInputModel>();
  const isEditMode = !!watch("id");

  const unitOptions: ISelectFieldModel[] = [
    { id: UnitOfMeasure.Piece, name: UnitOfMeasureLabels[UnitOfMeasure.Piece] },
    { id: UnitOfMeasure.Gram, name: UnitOfMeasureLabels[UnitOfMeasure.Gram] },
  ];

  const categoryOptions: ISelectFieldModel[] = categories.map((c) => ({
    id: c.id,
    name: c.name,
  }));

  return (
    <div className={styles.root}>
      <DefaultInputField
        name={nameof<IInventoryItemInputModel>((x) => x.name)}
        label="Име на продукта"
        placeholder="Вода 0.5л"
        required
      />

      <DefaultFormSelectField
        name={nameof<IInventoryItemInputModel>((x) => x.categoryId)}
        label="Категория"
        options={categoryOptions}
        emptyOptionLabel="Без категория"
        includeEmptyOption
        includeId={false}
      />

      <DefaultInputField
        name={nameof<IInventoryItemInputModel>((x) => x.brand)}
        label="Марка"
        placeholder="Незадължително"
        tooltip="Марка или производител на продукта. Ако е зададена, ще се показва като 'Име - Марка' навсякъде."
      />

      <DefaultInputField
        name={nameof<IInventoryItemInputModel>((x) => x.sku)}
        label="SKU"
        placeholder="Незадължително"
        tooltip="Уникален код от производителя (Stock Keeping Unit). Използва се за идентификация на продукта при доставки и поръчки."
      />

      <BarcodeField
        name={nameof<IInventoryItemInputModel>((x) => x.barcode)}
        label="Баркод"
        placeholder="Сканирай или въведи"
        tooltip="Натиснете иконата на камерата за сканиране, или просто кликнете върху полето и сканирайте с USB четец."
      />

      <DefaultInputField
        name={nameof<IInventoryItemInputModel>((x) => x.price)}
        label="Цена"
        placeholder="0"
        type="number"
        min={0}
        step={0.01}
        currency={CurrencyType.EUR}
        required
      />

      <DefaultInputField
        name={nameof<IInventoryItemInputModel>((x) => x.costPrice)}
        label="Себестойност"
        placeholder="0"
        type="number"
        min={0}
        step={0.01}
        currency={CurrencyType.EUR}
        required
      />

      <DefaultFormSelectField
        name={nameof<IInventoryItemInputModel>((x) => x.unitOfMeasure)}
        label="Мерна единица"
        options={unitOptions}
        includeId={false}
        disabled={isEditMode}
        tooltip={
          isEditMode
            ? "Мерната единица не може да бъде променяна след създаване на продукта."
            : "Мерната единица се задава веднъж и не може да бъде променяна."
        }
        required
      />

      <DefaultInputField
        name={nameof<IInventoryItemInputModel>((x) => x.initialQuantity)}
        label="Начално количество"
        placeholder="0"
        type="number"
        min={0}
        step={0.001}
        disabled={isEditMode}
        tooltip={
          isEditMode
            ? "Началното количество се задава веднъж и не може да бъде променяно. Използвайте доставки или корекция за промяна на наличността."
            : "Задайте наличността в момента на добавянето на продукта. Тази стойност не може да бъде променяна по-късно."
        }
        required
      />

      <DefaultInputField
        name={nameof<IInventoryItemInputModel>((x) => x.lowStockThreshold)}
        label="Праг за ниска наличност"
        placeholder="5"
        type="number"
        min={0}
        step={0.001}
        tooltip="Под тази стойност продуктът ще бъде маркиран като 'ниска наличност'."
        required
      />

      <DefaultTextareaField
        name={nameof<IInventoryItemInputModel>((x) => x.description)}
        label="Описание"
        placeholder="Незадължително"
        rows={3}
      />
    </div>
  );
};
